import React, { useState } from 'react';
import { MetricRow, SelectedMetric, ExportFormat, MetricStats } from '../types';
import Plotly from 'plotly.js';

interface ExportPanelProps {
  data: MetricRow[];
  selectedMetrics: SelectedMetric[];
  stats: Record<string, MetricStats>;
  datasetName: string;
  visible: boolean;
  onClose: () => void;
  plotRef?: React.RefObject<any>;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'rgba(33, 40, 44, 0.98)',
    borderRadius: '12px',
    border: '1px solid rgba(108, 117, 125, 0.3)',
    width: '450px',
    maxWidth: '90vw',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.25rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.2)',
  },
  title: {
    color: '#c5c5c5',
    fontSize: '1rem',
    fontWeight: 500,
    margin: 0,
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: '#8b949e',
    cursor: 'pointer',
    padding: '0.25rem',
  },
  content: {
    padding: '1.25rem',
  },
  formatGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  formatButton: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem',
    borderRadius: '8px',
    border: '1px solid rgba(108, 117, 125, 0.3)',
    background: 'rgba(33, 40, 44, 0.6)',
    color: '#c5c5c5',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  formatButtonActive: {
    borderColor: '#53bde4',
    background: 'rgba(83, 189, 228, 0.15)',
  },
  formatIcon: {
    fontSize: '1.5rem',
  },
  formatLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  formatDesc: {
    fontSize: '0.7rem',
    color: '#6c757d',
  },
  section: {
    marginBottom: '1.25rem',
  },
  sectionTitle: {
    color: '#8b949e',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.5rem',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    color: '#c5c5c5',
    fontSize: '0.85rem',
  },
  checkboxInput: {
    width: '16px',
    height: '16px',
    accentColor: '#53bde4',
  },
  info: {
    padding: '0.75rem',
    background: 'rgba(83, 189, 228, 0.1)',
    borderRadius: '6px',
    color: '#8b949e',
    fontSize: '0.8rem',
    marginBottom: '1rem',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.25rem',
    borderTop: '1px solid rgba(108, 117, 125, 0.2)',
  },
  cancelButton: {
    padding: '0.6rem 1.25rem',
    borderRadius: '6px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'transparent',
    color: '#8b949e',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  exportButton: {
    padding: '0.6rem 1.5rem',
    borderRadius: '6px',
    border: 'none',
    background: '#53bde4',
    color: '#1a1a1a',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
};

const FORMAT_OPTIONS: { value: ExportFormat; label: string; icon: string; description: string }[] = [
  { value: 'csv', label: 'CSV', icon: '📊', description: 'Spreadsheet compatible' },
  { value: 'json', label: 'JSON', icon: '{ }', description: 'Raw data with metadata' },
  { value: 'png', label: 'PNG', icon: '🖼️', description: 'Chart image (high-res)' },
  { value: 'svg', label: 'SVG', icon: '📐', description: 'Vector graphics' },
];

export function ExportPanel({
  data,
  selectedMetrics,
  stats,
  datasetName,
  visible,
  onClose,
  plotRef,
}: ExportPanelProps) {
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [includeStats, setIncludeStats] = useState(true);
  const [selectedMetricsOnly, setSelectedMetricsOnly] = useState(true);

  if (!visible) return null;

  const visibleMetrics = selectedMetrics.filter(m => m.visible);
  const metricsToExport = selectedMetricsOnly ? visibleMetrics : selectedMetrics;

  const handleExport = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${datasetName.replace(/[^a-z0-9]/gi, '_')}_${timestamp}`;

    switch (format) {
      case 'csv':
        exportCSV(filename);
        break;
      case 'json':
        exportJSON(filename);
        break;
      case 'png':
        exportImage('png', filename);
        break;
      case 'svg':
        exportImage('svg', filename);
        break;
    }

    onClose();
  };

  const exportCSV = (filename: string) => {
    const metricKeys = metricsToExport.map(m => m.key);

    // Header row
    const headers = ['timestamp', 'deviceId', ...metricKeys];

    // Data rows
    const rows = data.map(row => {
      const timestamp = row.createdAt;
      const deviceId = row.deviceId ?? '';
      const values = metricKeys.map(key => {
        const val = row.metrics?.[key];
        return val !== undefined && val !== null ? String(val) : '';
      });
      return [timestamp, deviceId, ...values];
    });

    // Add stats section if requested
    let csvContent = headers.join(',') + '\n';
    csvContent += rows.map(row =>
      row.map(cell => {
        const str = String(cell);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(',')
    ).join('\n');

    if (includeStats) {
      csvContent += '\n\n# Statistics\n';
      csvContent += 'metric,min,max,avg,stdDev,count\n';
      for (const metric of metricsToExport) {
        const s = stats[metric.key];
        if (s) {
          csvContent += `${metric.key},${s.min},${s.max},${s.avg},${s.stdDev},${s.count}\n`;
        }
      }
    }

    downloadFile(csvContent, `${filename}.csv`, 'text/csv');
  };

  const exportJSON = (filename: string) => {
    const metricKeys = metricsToExport.map(m => m.key);

    const exportData: any = {
      metadata: {
        datasetName,
        exportedAt: new Date().toISOString(),
        totalRecords: data.length,
        metrics: metricKeys,
      },
      data: data.map(row => ({
        timestamp: row.createdAt,
        deviceId: row.deviceId,
        metrics: Object.fromEntries(
          metricKeys.map(key => [key, row.metrics?.[key] ?? null])
        ),
      })),
    };

    if (includeStats) {
      exportData.statistics = Object.fromEntries(
        metricsToExport
          .filter(m => stats[m.key])
          .map(m => [m.key, stats[m.key]])
      );
    }

    downloadFile(JSON.stringify(exportData, null, 2), `${filename}.json`, 'application/json');
  };

  const exportImage = async (type: 'png' | 'svg', filename: string) => {
    // Use Plotly's built-in image export
    const plotElement = document.querySelector('.js-plotly-plot');
    if (!plotElement) {
      console.error('Plot element not found');
      return;
    }

    try {
      const imageData = await Plotly.toImage(plotElement as any, {
        format: type,
        width: 1920,
        height: 1080,
        scale: type === 'png' ? 2 : 1, // Higher resolution for PNG
      });

      // Download the image
      const link = document.createElement('a');
      link.href = imageData;
      link.download = `${filename}.${type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export image:', err);
    }
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>Export Data</h3>
          <button style={styles.closeButton} onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Format selection */}
          <div style={styles.formatGrid}>
            {FORMAT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setFormat(opt.value)}
                style={{
                  ...styles.formatButton,
                  ...(format === opt.value ? styles.formatButtonActive : {}),
                }}
              >
                <span style={styles.formatIcon}>{opt.icon}</span>
                <span style={styles.formatLabel}>{opt.label}</span>
                <span style={styles.formatDesc}>{opt.description}</span>
              </button>
            ))}
          </div>

          {/* Options for data exports */}
          {(format === 'csv' || format === 'json') && (
            <>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Options</div>
                <label style={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={selectedMetricsOnly}
                    onChange={e => setSelectedMetricsOnly(e.target.checked)}
                    style={styles.checkboxInput}
                  />
                  Only export visible metrics ({visibleMetrics.length} of {selectedMetrics.length})
                </label>
                <label style={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={includeStats}
                    onChange={e => setIncludeStats(e.target.checked)}
                    style={styles.checkboxInput}
                  />
                  Include statistics (min, max, avg, stdDev)
                </label>
              </div>

              <div style={styles.info}>
                <strong>{data.length.toLocaleString()}</strong> data points will be exported
                {selectedMetricsOnly && ` for ${visibleMetrics.length} metrics`}.
              </div>
            </>
          )}

          {/* Info for image exports */}
          {(format === 'png' || format === 'svg') && (
            <div style={styles.info}>
              The current chart view will be exported as a {format === 'png' ? '1920x1080 PNG' : 'scalable SVG'} image.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button style={styles.exportButton} onClick={handleExport}>
            Export {format.toUpperCase()}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportPanel;
